import { mapGlobalToEquipments } from '@pages/Products/components/OnBoardingModal/helpers/mapGlobalToEquipments';
import { mapSinglesToEquipments } from '@pages/Products/components/OnBoardingModal/helpers/mapSinglesToEquipments';
import {
  ActorRefFrom,
  assign,
  createMachine,
  ExtractEvent,
  InterpreterFrom,
  Prop,
  StateFrom,
} from 'xstate';
import { pure, send } from 'xstate/lib/actions';
import { EquipmentsMap } from './equipmentsPicker.schema';
import {
  MassEditContext as Context,
  EditActions,
  MassEditEvents as Events,
  MassEditSchemaValue,
  TERMINATE_REASON,
} from './massEdit.schema';

// default: https://xstate.js.org/docs/guides/communication.html#invoking-with-context
const initialContext: Context = {
  actionQueue: [],
  equipments: new Map(),
  isDirty: false,
  terminateReason: null,
};

export const massEdit = createMachine(
  {
    id: 'mass-single-edit',
    initial: 'open',
    states: {
      closed: {
        type: 'final',
        entry: 'onClose',
        description:
          'Onclose here, decides what to set for onClose based on the terminate type it receives',
      },
      open: {
        type: 'parallel',
        states: {
          closing: {
            initial: 'idle',
            states: {
              idle: {
                description: 'send save events after prompt',
                on: {
                  TERMINATE: [
                    {
                      target: 'prompt',
                      cond: 'shouldPrompt',
                      actions: 'setTerminateReason',
                      description:
                        'set prompt based off of event terminate name and guard.',
                    },
                    {
                      target: '#mass-single-edit.closed',
                    },
                  ],
                },
              },
              prompt: {
                entry: 'toggleModal',
                on: {
                  'SAVE-FIRST': {
                    target: 'idle',
                    actions: 'sendSaveEvent',
                    description:
                      'Constructing a save-then-close with the ctx currently available.',
                  },
                  ABORT: {
                    target: 'idle',
                  },
                  'FORCE-TERMINATE': {
                    target: '#mass-single-edit.closed',
                  },
                },
              },
            },
          },
          editing: {
            initial: 'idle',
            states: {
              idle: {
                on: {
                  SAVE: {
                    target: 'saving',
                    cond: 'isDirty',
                  },
                  'SAVE-THEN-CLOSE': {
                    target: 'saving',
                    cond: 'isDirty',
                    actions: 'queueClose',
                  },
                },
              },
              saving: {
                invoke: {
                  src: 'doSave',
                  id: 'doSave',
                },
                on: {
                  'SAVE-THEN-CLOSE': {
                    cond: 'isDirty',
                    actions: ['queueClose', 'queueSave'],
                  },
                  SUCCESS: {
                    target: 'checking for pending save action',
                    actions: 'updateEquipments',
                    description:
                      'Action takes in known data, mode and params sent out.',
                  },
                  ERROR: {
                    target: 'checking for pending save action',
                    actions: 'unqueueClose',
                  },
                  SAVE: {
                    cond: 'isDirty',
                    actions: 'queueSave',
                    description:
                      "nb. queuing here also replaces actions for the save as we won't execute them until saved.",
                  },
                },
              },
              'checking for pending save action': {
                always: [
                  {
                    target: 'saving',
                    cond: 'hasSaveQueued',
                    actions: 'unqueueSave',
                  },
                  {
                    target: 'idle',
                    actions: ['emitCloseAction', 'unqueueClose'],
                    description: 'Emit also unqueues when done.',
                  },
                ],
              },
            },
          },
        },
        on: {
          DIRTY: [
            {
              cond: 'willDirty',
              actions: ['syncDirty', 'unqueueClose'],
              description:
                "Unqueues close if the user makes a change while saving for save then close to avoid the prompt.\n\nNb: Prompt will still show up if saving, but that isn't affected here.",
            },
            {
              actions: 'syncDirty',
            },
          ],
        },
      },
    },
    context: initialContext,
    tsTypes: {} as import('./massEdit.typegen').Typegen0,
    schema: { ...MassEditSchemaValue },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      unqueueClose: assign({
        actionQueue: context => unqueue(context, 'CLOSE'),
      }),

      setTerminateReason: assign({
        terminateReason: (_, event) => event.reason,
      }),
      queueClose: assign({
        actionQueue: (context, event) => {
          return queue(context, {
            type: 'CLOSE',
            kind: reasonToCloseKind(event.reason),
          });
        },
      }),

      queueSave: assign({
        actionQueue: context => queue(context, { type: 'SAVE' }),
      }),
      unqueueSave: assign({
        actionQueue: context => unqueue(context, 'SAVE'),
      }),
      syncDirty: assign({
        isDirty: (_, event) => event.isDirty,
      }),
      emitCloseAction: pure(context => {
        const { actionQueue } = context;
        const action = actionQueue.find(_action => _action.type === 'CLOSE') as
          | ExtractEvent<EditActions, 'CLOSE'>
          | undefined;
        if (!action) return undefined;

        return send({
          type: 'TERMINATE',
          isSaving: false,
          reason: kindToReason(action.kind),
        } as Events);
      }),
      sendSaveEvent: pure(context => {
        return send({
          type: 'SAVE-THEN-CLOSE',
          reason: context.terminateReason,
        } as Events);
      }),
      updateEquipments: assign({
        equipments: (context, event) => {
          const { equipments } = context;
          const { values, knownData, tagsDiff = [] } = event;
          let map: EquipmentsMap;
          if (Array.isArray(values)) {
            map = mapSinglesToEquipments(values, equipments, knownData);
          } else {
            map = mapGlobalToEquipments(
              values,
              equipments,
              knownData,
              tagsDiff,
            );
          }

          return map;
        },
      }),
    },
    guards: {
      shouldPrompt: (context, event) => context.isDirty || event.isSaving,
      isDirty: context => context.isDirty,
      willDirty: (_, event) => event.isDirty,
      hasSaveQueued: context => hasAction(context.actionQueue, 'SAVE'),
    },
  },
);
export type MassEditActorRef = ActorRefFrom<typeof massEdit>;
export type MassEditInterpreter = InterpreterFrom<typeof massEdit>;

/**
 * Queue action , and remove any actions of the same type in the list.
 */
function queue(context: Context, action: EditActions) {
  const actionQueue = unqueue(context, action.type);
  return [...actionQueue, action];
}
/**
 * Unqueue all actions matching the type.
 */
function unqueue(context: Context, type: EditActions['type']) {
  if (!hasAction(context.actionQueue, type)) return context.actionQueue;
  return context.actionQueue.filter(_action => _action.type !== type);
}
/**
 * Check if an action of a particular type exists.
 */
export function hasAction(
  actionQueue: Context['actionQueue'],
  type: EditActions['type'],
) {
  return actionQueue.some(_action => _action.type === type);
}
function reasonToCloseKind(
  reason: TERMINATE_REASON,
): ExtractEvent<EditActions, 'CLOSE'>['kind'] {
  if (reason === 'ADD-MORE') return 'CLOSE-ADDMORE';
  return 'CLOSE';
}
function kindToReason(
  kind: ReturnType<typeof reasonToCloseKind>,
): TERMINATE_REASON {
  if (kind === 'CLOSE-ADDMORE') return 'ADD-MORE';
  return null;
}

type STATE_TYPE = StateFrom<typeof massEdit>;
type SEND_TYPE = Prop<InterpreterFrom<typeof massEdit>, 'send'>;

export type {
  STATE_TYPE as MASS_EDIT_STATE_TYPE,
  SEND_TYPE as MASS_EDIT_SEND_TYPE,
};
